/**
 * Connect modal — opens when a user clicks "Connect" on the Connections page.
 * Per platform, asks for the credentials, validates server-side, and persists.
 *
 * Stage 1 platforms (UltraCart + HasOffers) use API keys, not OAuth. The
 * server-side connect service validates the key against the platform's
 * health endpoint before saving (Engineering Principle #5: never accept
 * bad config silently).
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import type { CatalogProvider } from "../lib/integration-catalog.js";
import { CONNECTION_GUIDES } from "../content/connection-guides.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

interface ConnectModalProps {
  platform: string;
  /** The catalog descriptor — drives the ESP form (fields) + the title. */
  provider?: CatalogProvider;
  /** Konnektive reconnects can validate the encrypted saved login without
   * putting credentials back in the browser. */
  reconnect?: { integrationId: string };
  onClose: () => void;
  onSuccess: () => void;
}

export interface OAuthLoginChoice {
  id: string;
  label: string;
  urlPath: string;
}

/** Keep the existing one-button OAuth contract for every provider while a
 * provider such as Microsoft Ads can offer more than one familiar sign-in. */
export function oauthLoginChoices(provider: CatalogProvider): OAuthLoginChoice[] {
  if (provider.oauthChoices && provider.oauthChoices.length > 0) {
    return provider.oauthChoices;
  }
  if (!provider.oauthUrlPath) return [];
  return [
    {
      id: "default",
      label: `Log in with ${provider.label.split(" ")[0]}`,
      urlPath: provider.oauthUrlPath,
    },
  ];
}

export function OAuthLoginButtons({
  choices,
  startingId,
  disabled = false,
  fullWidth = false,
  onStart,
}: {
  choices: OAuthLoginChoice[];
  startingId: string | null;
  disabled?: boolean;
  fullWidth?: boolean;
  onStart: (choice: OAuthLoginChoice) => void;
}): React.ReactElement {
  return (
    <>
      {choices.map((choice) => (
        <button
          key={choice.id}
          type={choices.length === 1 ? "submit" : "button"}
          style={{ ...styles.submit, ...(fullWidth ? { width: "100%" } : {}) }}
          disabled={disabled || startingId !== null}
          onClick={choices.length === 1 ? undefined : () => onStart(choice)}
        >
          {startingId === choice.id ? "Opening login…" : choice.label}
        </button>
      ))}
    </>
  );
}

const LABELS: Record<string, string> = { ultracart: "UltraCart", hasoffers: "HasOffers" };

export default function ConnectModal({ platform, provider, reconnect, onClose, onSuccess }: ConnectModalProps): React.ReactElement {
  const savedKonnektiveReconnect = platform === "konnektive" && reconnect;
  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={{ margin: 0, fontSize: 18 }}>
            {savedKonnektiveReconnect ? "Reconnect Konnektive" : `Connect ${provider?.label ?? LABELS[platform] ?? platform}`}
          </h2>
          <button style={styles.close} onClick={onClose}>
            ×
          </button>
        </div>
        {platform === "ultracart" ? (
          <UltraCartForm onClose={onClose} onSuccess={onSuccess} />
        ) : platform === "hasoffers" ? (
          <HasOffersForm onClose={onClose} onSuccess={onSuccess} />
        ) : provider?.connectKind === "esp" ? (
          <EspForm provider={provider} onClose={onClose} onSuccess={onSuccess} />
        ) : provider?.connectKind === "fields" ? (
          savedKonnektiveReconnect ? (
            <KonnektiveReconnectForm
              integrationId={savedKonnektiveReconnect.integrationId}
              provider={provider}
              onClose={onClose}
              onSuccess={onSuccess}
            />
          ) : (
            <FieldsForm provider={provider} onClose={onClose} onSuccess={onSuccess} />
          )
        ) : null}
      </div>
    </div>
  );
}

/** Only an explicit, safe backend code means the saved API login itself was
 * rejected. Network/server/conflict failures keep the blank credential form
 * hidden so a temporary problem never asks the user to replace a good login. */
export function isSavedKonnektiveLoginRejected(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const status = (error as { status?: unknown }).status;
  const detail = (error as { detail?: unknown }).detail;
  return status === 422
    && Boolean(detail)
    && typeof detail === "object"
    && (detail as { code?: unknown }).code === "saved_credentials_rejected";
}

function KonnektiveReconnectForm({
  integrationId,
  provider,
  onClose,
  onSuccess,
}: {
  integrationId: string;
  provider: CatalogProvider;
  onClose: () => void;
  onSuccess: () => void;
}): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const reconnect = useMutation({
    mutationFn: () => api.post(`/integrations/${integrationId}/reconnect-saved`),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onSuccess();
      onClose();
    },
    onError: (error: unknown) => {
      if (isSavedKonnektiveLoginRejected(error)) setShowCredentialForm(true);
    },
  });

  return (
    <KonnektiveReconnectView
      provider={provider}
      showCredentialForm={showCredentialForm}
      retrying={reconnect.isPending}
      retryFailed={Boolean(reconnect.error) && !showCredentialForm}
      onRetry={() => reconnect.mutate()}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
}

export function KonnektiveReconnectView({
  provider,
  showCredentialForm,
  retrying,
  retryFailed,
  onRetry,
  onClose,
  onSuccess,
}: {
  provider: CatalogProvider;
  showCredentialForm: boolean;
  retrying: boolean;
  retryFailed: boolean;
  onRetry: () => void;
  onClose: () => void;
  onSuccess: () => void;
}): React.ReactElement {
  if (showCredentialForm) {
    return (
      <FieldsForm
        provider={provider}
        mode="update"
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <div style={styles.form}>
      {retryFailed && (
        <p style={styles.error}>
          We couldn’t check your saved login right now. Try again in a minute. Nothing changed.
        </p>
      )}
      <div style={styles.actions}>
        <button type="button" style={styles.cancel} onClick={onClose}>
          Cancel
        </button>
        <button type="button" style={styles.submit} disabled={retrying} onClick={onRetry}>
          {retrying ? "Reconnecting…" : "Reconnect"}
        </button>
      </div>
    </div>
  );
}

/**
 * "Where do I find this?" — expandable step-by-step guide (the client
 * knowledge base, founder 2026-07-04). Steps come from the verified guides
 * content module; the official-help link carries the platform's own
 * screenshots. Rendered inside every connect form.
 */
export function GuidePanel({ platform, loginAvailable }: { platform: string; loginAvailable?: boolean }): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const guide = CONNECTION_GUIDES[platform];
  if (!guide) return null;
  // When the platform's login button is actually rendered, show the login
  // walkthrough; otherwise the manual-keys path (never "click the button
  // above" when there is no button above).
  const steps = loginAvailable && guide.loginSteps ? guide.loginSteps : guide.steps;

  return (
    <div style={guideStyles.wrap}>
      <button type="button" style={guideStyles.toggle} onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} Where do I find this? Step-by-step
      </button>
      {open && (
        <div style={guideStyles.panel}>
          <ol style={guideStyles.list}>
            {steps.map((s, i) => (
              <li key={i} style={guideStyles.step}>
                {s}
              </li>
            ))}
          </ol>
          {guide.note && <p style={guideStyles.note}>{guide.note}</p>}
          {guide.officialHelp && (
            <p style={guideStyles.note}>
              Want screenshots?{" "}
              <a href={guide.officialHelp.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>
                {guide.officialHelp.label} ↗
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const guideStyles: Record<string, React.CSSProperties> = {
  wrap: { marginTop: -4 },
  toggle: {
    background: "transparent",
    border: "none",
    color: "var(--accent)",
    fontSize: 13,
    cursor: "pointer",
    padding: 0,
    textAlign: "left",
  },
  panel: {
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "12px 14px",
    marginTop: 8,
  },
  list: { margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 },
  step: { fontSize: 13, color: "var(--text)", lineHeight: 1.5 },
  note: { fontSize: 12, color: "var(--body)", margin: "10px 0 0" },
};

function UltraCartForm({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");

  const connect = useMutation({
    mutationFn: () => api.post("/integrations/ultracart/connect", { apiKey }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onSuccess();
      onClose();
    },
  });

  const error = connect.error as { detail?: { error?: string } } | null;
  const errorMsg = error?.detail?.error ?? (connect.error ? String(connect.error) : null);

  return (
    <form
      style={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate();
      }}
    >
      <p style={styles.intro}>
        UltraCart uses a "Simple Key" for API access. Find yours under{" "}
        <span style={styles.path}>Configuration → API → REST API</span> in your UltraCart admin.
      </p>
      <GuidePanel platform="ultracart" />

      <label style={styles.label}>
        API Key (Simple Key)
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="usimplekey-..."
          style={styles.input}
          autoFocus
        />
      </label>

      {errorMsg && <p style={styles.error}>{errorMsg}</p>}

      <div style={styles.actions}>
        <button type="button" style={styles.cancel} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" style={styles.submit} disabled={!apiKey || connect.isPending}>
          {connect.isPending ? "Validating…" : "Connect"}
        </button>
      </div>
    </form>
  );
}

function HasOffersForm({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [networkId, setNetworkId] = useState("");
  const [networkToken, setNetworkToken] = useState("");
  const [baseUrl, setBaseUrl] = useState("");

  const connect = useMutation({
    mutationFn: () =>
      api.post("/integrations/hasoffers/connect", { networkId, networkToken, baseUrl }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onSuccess();
      onClose();
    },
  });

  const error = connect.error as { detail?: { error?: string } } | null;
  const errorMsg = error?.detail?.error ?? (connect.error ? String(connect.error) : null);

  return (
    <form
      style={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate();
      }}
    >
      <p style={styles.intro}>
        HasOffers (TUNE) needs three things from the API tab in your network
        admin: your Network ID, your Network Token, and the API Domain shown there.
      </p>
      <GuidePanel platform="hasoffers" />

      <label style={styles.label}>
        Network ID
        <input
          type="text"
          value={networkId}
          onChange={(e) => setNetworkId(e.target.value)}
          placeholder="acmebrand"
          style={styles.input}
          autoFocus
        />
        <span style={styles.optional}>
          The short name in your TUNE web address — the "acmebrand" in acmebrand.hasoffers.com.
        </span>
      </label>

      <label style={styles.label}>
        Network Token
        <input
          type="password"
          value={networkToken}
          onChange={(e) => setNetworkToken(e.target.value)}
          placeholder="NETabc123..."
          style={styles.input}
        />
      </label>

      <label style={styles.label}>
        API Domain
        {/* type="text", not "url": the browser's URL validation would reject the
            exact bare-domain value the guide tells people to paste. The server
            normalizes https://, trailing slashes, and pasted endpoints. */}
        <input
          type="text"
          inputMode="url"
          autoCapitalize="none"
          spellCheck={false}
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="acmebrand.api.hasoffers.com"
          style={styles.input}
        />
      </label>

      {errorMsg && <p style={styles.error}>{errorMsg}</p>}

      <div style={styles.actions}>
        <button type="button" style={styles.cancel} onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          style={styles.submit}
          disabled={!networkId || !networkToken || !baseUrl || connect.isPending}
        >
          {connect.isPending ? "Validating…" : "Connect"}
        </button>
      </div>
    </form>
  );
}

/**
 * Generic ESP connect form — renders inputs straight from the platform's
 * credential field schema (fetched from the backend registry). One component
 * for Klaviyo (1 key), Ontraport (App ID + key), and any future ESP — the
 * fields, labels, secrecy, and validation all come from the descriptor, so the
 * form can never drift from what the server expects.
 */
function EspForm({
  provider,
  onClose,
  onSuccess,
}: {
  provider: CatalogProvider;
  onClose: () => void;
  onSuccess: () => void;
}): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const fields = provider.fields ?? [];

  const connect = useMutation({
    mutationFn: () => api.post(`/integrations/esp/${provider.platform}/connect`, { credentials: values }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onSuccess();
      onClose();
    },
  });

  const error = connect.error as { detail?: { error?: string } } | null;
  const errorMsg = error?.detail?.error ?? (connect.error ? String(connect.error) : null);
  const allFilled = fields.every((f) => (values[f.key] ?? "").trim().length > 0);

  return (
    <form
      style={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate();
      }}
    >
      <p style={styles.intro}>
        {provider.instructions} We use it only to push your segments to {provider.label} — never to read customer data in.
      </p>
      <GuidePanel platform={provider.platform} />

      {fields.map((f, i) => (
        <label key={f.key} style={styles.label}>
          {f.label}
          <input
            type={f.secret ? "password" : "text"}
            value={values[f.key] ?? ""}
            onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            placeholder={f.placeholder ?? ""}
            style={styles.input}
            autoFocus={i === 0}
          />
          {f.help && <span style={styles.optional}>{f.help}</span>}
        </label>
      ))}

      {errorMsg && <p style={styles.error}>{errorMsg}</p>}

      <div style={styles.actions}>
        <button type="button" style={styles.cancel} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" style={styles.submit} disabled={!allFilled || connect.isPending}>
          {connect.isPending ? "Validating…" : "Connect"}
        </button>
      </div>
    </form>
  );
}

/**
 * Generic data-pull connect form (ad platforms, affiliate networks, …) —
 * same descriptor-driven rendering as EspForm, but posts to the fields
 * connect route and skips the ESP-specific "we only push" line: these
 * connections DO read data in (that's their whole job).
 */
function FieldsForm({
  provider,
  mode = "connect",
  onClose,
  onSuccess,
}: {
  provider: CatalogProvider;
  mode?: "connect" | "update";
  onClose: () => void;
  onSuccess: () => void;
}): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [oauthStarting, setOauthStarting] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [showWhiteGloveFields, setShowWhiteGloveFields] = useState(false);
  const [showManualFields, setShowManualFields] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [webhookSetupUrl, setWebhookSetupUrl] = useState<string | null>(null);
  const [copiedSetupUrl, setCopiedSetupUrl] = useState(false);
  const fields = provider.fields ?? [];
  const submitLabel = mode === "update" ? "Update API login" : "Connect";
  const pendingLabel = mode === "update" ? "Checking new login…" : "Validating…";

  // One durable "ask us" channel for every dead-end state (white-glove,
  // login-pending, login-planned): files a connection_requests row the
  // agency console sees. Never a mailto — nothing else in the app has one.
  const request = useMutation({
    mutationFn: (note: string) =>
      api.post("/integrations/connection-request", { platformName: provider.label, note }),
    onSuccess: () => setRequestSent(true),
  });

  const connect = useMutation({
    mutationFn: () => api.post<{
      setup?: { kind: "webhook"; url?: string };
    }>(`/integrations/fields/${provider.platform}/connect`, { credentials: values }),
    onSuccess: (result) => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      if (result.setup?.kind === "webhook" && result.setup.url) {
        setWebhookSetupUrl(result.setup.url);
        return;
      }
      onSuccess();
      onClose();
    },
  });

  if (webhookSetupUrl) {
    return (
      <div style={styles.form}>
        <p style={styles.intro}>
          Your private BuyGoods link is ready. Finish these steps in BuyGoods:
        </p>
        <ol style={{ ...guideStyles.list, margin: 0 }}>
          <li style={guideStyles.step}>Go to Setup, then Products, then Settings, and open the IPN section.</li>
          <li style={guideStyles.step}>Paste the same link below into New Order, Rebill, Refund, and Chargeback.</li>
          <li style={guideStyles.step}>Save the settings.</li>
          <li style={guideStyles.step}>Export your full order history once as a CSV so our team can reconcile the past before approving your numbers.</li>
        </ol>
        <div style={{ display: "flex", gap: 8 }}>
          <input readOnly value={webhookSetupUrl} style={{ ...styles.input, flex: 1 }} aria-label="Private BuyGoods callback link" />
          <button
            type="button"
            style={styles.cancel}
            onClick={() => {
              void navigator.clipboard.writeText(webhookSetupUrl).then(() => setCopiedSetupUrl(true));
            }}
          >
            {copiedSetupUrl ? "Copied" : "Copy"}
          </button>
        </div>
        <p style={styles.hint}>
          This link is private. Do not post it publicly. New activity starts flowing automatically after BuyGoods saves it.
        </p>
        <div style={styles.actions}>
          <button
            type="button"
            style={styles.submit}
            onClick={() => {
              onSuccess();
              onClose();
            }}
          >
            Done for now
          </button>
        </div>
      </div>
    );
  }

  // Fields the login button needs BEFORE it can start (Shopify's store
  // domain, GA4's property id). They render
  // ABOVE the login button; everything else is manual-keys-only and hides
  // behind an expander so the natural first click is always the right one.
  const preLoginParams = provider.oauthFieldParams ?? (provider.oauthShopParam ? [provider.oauthShopParam] : []);
  const preLoginKeys = new Set(preLoginParams.map((p) => p.fromFieldKey));
  const loginChoices = oauthLoginChoices(provider);
  const loginReady = loginChoices.length > 0;
  const preFields = loginReady ? fields.filter((f) => preLoginKeys.has(f.key)) : [];
  const manualFields = loginReady ? fields.filter((f) => !preLoginKeys.has(f.key)) : fields;
  const missingPreField = preFields.find((f) => !(values[f.key] ?? "").trim());

  // OAuth login button (when the server says it's configured): fetch the
  // platform's login URL and send the browser there. The platform redirects
  // back to the Connections page, which finishes the flow.
  const startOAuth = async (choice: OAuthLoginChoice): Promise<void> => {
    // Some platforms need values the user types first baked into the login
    // URL — for example, Shopify's store domain. Google Ads and Microsoft Ads
    // discover the available ad accounts after login, so clients type no IDs.
    // Require every named field before we start.
    let path = choice.urlPath;
    if (preLoginParams.length > 0) {
      const qs = new URLSearchParams();
      for (const p of preLoginParams) {
        const val = (values[p.fromFieldKey] ?? "").trim();
        if (!val) {
          const missing = fields.find((f) => f.key === p.fromFieldKey)?.label ?? p.fromFieldKey;
          setOauthError(`Enter your ${missing} first, then click Log in.`);
          return;
        }
        qs.set(p.queryKey, val);
      }
      path += `?${qs.toString()}`;
    }
    setOauthStarting(choice.id);
    setOauthError(null);
    try {
      const res = await api.get<{ available: boolean; url?: string; error?: string }>(path);
      if (res.available && res.url) {
        window.location.href = res.url;
        return; // navigating away
      }
      setOauthError(
        res.error ??
          (provider.oauthOnly
            ? "Login couldn't start. Try again."
            : "Login isn't set up yet. Use the manual connection below instead."),
      );
    } catch {
      setOauthError(
        provider.oauthOnly
          ? "Login couldn't start. Try again."
          : "Couldn't start the login. Use the manual connection below instead.",
      );
    }
    setOauthStarting(null);
  };

  const error = connect.error as { detail?: { error?: string } } | null;
  const errorMsg = error?.detail?.error ?? (connect.error ? String(connect.error) : null);
  const allFilled = fields.every((f) => f.optional || (values[f.key] ?? "").trim().length > 0);
  const manualEmpty = manualFields.every((f) => !(values[f.key] ?? "").trim());
  const renderField = (f: NonNullable<CatalogProvider["fields"]>[number], autoFocus: boolean): React.ReactElement => (
    <label key={f.key} style={styles.label}>
      {f.label}
      <input
        type={f.secret ? "password" : "text"}
        value={values[f.key] ?? ""}
        onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
        placeholder={f.placeholder ?? ""}
        style={styles.input}
        autoFocus={autoFocus}
      />
      {f.help && <span style={styles.optional}>{f.help}</span>}
    </label>
  );

  // We-handle-it connections: request-button-first. Fields stay behind an
  // expander for operators who already have credentials.
  if (provider.whiteGlove && !showWhiteGloveFields) {
    return (
      <div style={styles.form}>
        <p style={styles.intro}>{provider.instructions}</p>
        <GuidePanel platform={provider.platform} loginAvailable={false} />
        {requestSent ? (
          <p style={{ ...styles.intro, color: "var(--good)", fontWeight: 600 }}>
            Request saved. You’re done with this connection for now. Our team will review it and contact the account owner with the next step.
          </p>
        ) : (
          <button
            type="button"
            style={{ ...styles.submit, width: "100%" }}
            disabled={request.isPending}
            onClick={() => request.mutate("We-handle-it connection requested from the connect window. Review readiness and contact the account owner with the next valid step.")}
          >
            {request.isPending ? "Sending…" : `Request ${provider.label} connection`}
          </button>
        )}
        <div style={styles.actions}>
          <button
            type="button"
            style={{ ...styles.cancel, marginRight: "auto", fontSize: 12 }}
            onClick={() => setShowWhiteGloveFields(true)}
          >
            I already have credentials from Scale Advisors
          </button>
          <button type="button" style={styles.cancel} onClick={onClose}>
            Close and keep setting up
          </button>
        </div>
      </div>
    );
  }

  // Login-only platform whose login is not enabled: be direct rather than
  // promising that it is "almost ready."
  if (provider.oauthPending) {
    return (
      <div style={styles.form}>
        <p style={styles.intro}>
          {provider.label} login is not enabled yet. There is nothing for you to find or paste.
        </p>
        {requestSent ? (
          <p style={{ ...styles.intro, color: "var(--good)", fontWeight: 600 }}>
            Request sent. We'll email you when {provider.label} can be connected.
          </p>
        ) : (
          <button
            type="button"
            style={{ ...styles.submit, width: "100%" }}
            disabled={request.isPending}
            onClick={() =>
              request.mutate(`${provider.label} login is not enabled. Please prioritize it and email me when it can be connected.`)
            }
          >
            {request.isPending ? "Sending…" : "Request this connection"}
          </button>
        )}
        <div style={styles.actions}>
          <button type="button" style={styles.cancel} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  // Login planned but manual setup needs developer access the client won't
  // have (TikTok): request-a-link first, token paste behind an expander.
  // Auto-heals — the moment the login env vars land, oauthUrlPath appears
  // and the normal login-first form below takes over.
  if (provider.requestFirst && !loginReady && !showManualFields) {
    return (
      <div style={styles.form}>
        <p style={styles.intro}>
          The fastest way to connect {provider.label}: click the button below and we'll email you a
          connection link — approving it takes about 2 minutes, no keys or settings on your side.
        </p>
        <GuidePanel platform={provider.platform} loginAvailable={false} />
        {requestSent ? (
          <p style={{ ...styles.intro, color: "var(--good)", fontWeight: 600 }}>
            Request sent! We'll email you a connection link shortly.
          </p>
        ) : (
          <button
            type="button"
            style={{ ...styles.submit, width: "100%" }}
            disabled={request.isPending}
            onClick={() => request.mutate(`Send a ${provider.label} connection link — requested from the connect window.`)}
          >
            {request.isPending ? "Sending…" : `Request ${provider.label} connection`}
          </button>
        )}
        <div style={styles.actions}>
          <button
            type="button"
            style={{ ...styles.cancel, marginRight: "auto", fontSize: 12 }}
            onClick={() => setShowManualFields(true)}
          >
            I already have an API token
          </button>
          <button type="button" style={styles.cancel} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      style={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        // Enter should do what the visible primary button does: start the
        // login whenever it's the primary path, paste-connect otherwise.
        if (loginReady && (provider.oauthOnly || !showManualFields)) {
          if (loginChoices.length === 1) {
            void startOAuth(loginChoices[0]!);
          } else {
            setOauthError("Choose a sign-in option below.");
          }
        } else {
          connect.mutate();
        }
      }}
    >
      {loginReady && provider.oauthOnly ? (
        // ——— Login-only platform ———
        // With pre-login fields (for example, GA4's property id) the
        // fields come FIRST and the login button sits in the submit corner,
        // lighting up when they're filled — the layout can no longer invite
        // a too-early click. With no fields, the button IS the form.
        <>
          <p style={styles.intro}>{provider.instructions}</p>
          {preFields.map((f, i) => renderField(f, i === 0))}
          {preFields.length === 0 && (
            <OAuthLoginButtons
              choices={loginChoices}
              startingId={oauthStarting}
              fullWidth
              onStart={(choice) => void startOAuth(choice)}
            />
          )}
          <GuidePanel platform={provider.platform} loginAvailable />
          {oauthError && <p style={styles.error}>{oauthError}</p>}
          {preFields.length > 0 && missingPreField && (
            <p style={styles.hint}>Fill in your {missingPreField.label} above and the login button will light up.</p>
          )}
          <div style={styles.actions}>
            <button type="button" style={styles.cancel} onClick={onClose}>
              Cancel
            </button>
            {preFields.length > 0 && (
              <OAuthLoginButtons
                choices={loginChoices}
                startingId={oauthStarting}
                disabled={Boolean(missingPreField)}
                onStart={(choice) => void startOAuth(choice)}
              />
            )}
          </div>
        </>
      ) : loginReady ? (
        // ——— Dual-mode platform with the login switched on ———
        // Pre-login fields (store domain) sit right above the login button;
        // manual key fields hide behind an expander so "no keys needed"
        // and a wall of key fields never appear together.
        <>
          {preFields.map((f, i) => renderField(f, i === 0))}
          <button
            type="button"
            style={{ ...styles.submit, width: "100%" }}
            onClick={() => void startOAuth(loginChoices[0]!)}
            disabled={oauthStarting !== null || Boolean(missingPreField)}
          >
            {oauthStarting === loginChoices[0]!.id
              ? "Opening login…"
              : loginChoices[0]!.label}
          </button>
          {missingPreField && (
            <p style={styles.hint}>Fill in your {missingPreField.label} above and the login button will light up.</p>
          )}
          {oauthError && <p style={styles.error}>{oauthError}</p>}
          <GuidePanel platform={provider.platform} loginAvailable />
          <button type="button" style={guideStyles.toggle} onClick={() => setShowManualFields((v) => !v)}>
            {showManualFields ? "▾" : "▸"} Prefer to connect with pasted API keys instead?
          </button>
          {showManualFields && (
            <>
              <p style={styles.intro}>{provider.instructions}</p>
              {manualFields.map((f) => renderField(f, false))}
              {errorMsg && <p style={styles.error}>{errorMsg}</p>}
            </>
          )}
          <div style={styles.actions}>
            <button type="button" style={styles.cancel} onClick={onClose}>
              Cancel
            </button>
            {showManualFields && (
              <button type="submit" style={styles.submit} disabled={!allFilled || manualEmpty || connect.isPending}>
                {connect.isPending ? pendingLabel : submitLabel}
              </button>
            )}
          </div>
        </>
      ) : (
        // ——— Manual-only platform (no login button) ———
        <>
          {provider.oauthPlanned && !provider.oauthOnly && !provider.requestFirst && (
            <>
              <p style={styles.hint}>
                A one-click login for {provider.label} is coming soon. The steps below connect you
                today — or, if they look too technical, we can do it for you.
              </p>
              {/* Escape hatch — the manual steps for some platforms (e.g. Meta
                  Business System User tokens) are a developer task; a marketer
                  shouldn't dead-end here (QA 2026-07-06). */}
              {requestSent ? (
                <p style={{ ...styles.intro, color: "var(--good)", fontWeight: 600 }}>
                  Request sent! We'll help you connect {provider.label} shortly.
                </p>
              ) : (
                <button
                  type="button"
                  style={{ ...styles.submit, width: "100%", marginBottom: 6 }}
                  disabled={request.isPending}
                  onClick={() => request.mutate(`Help connecting ${provider.label} — the manual steps are too technical; requested from the connect window.`)}
                >
                  {request.isPending ? "Sending…" : `Have us connect ${provider.label} for you`}
                </button>
              )}
            </>
          )}
          {mode === "update" ? (
            <p style={styles.intro}>
              <strong style={{ color: "var(--text)" }}>Update your API login and password</strong>
              <br />
              Your saved login no longer works. Enter the current API Login ID and API Password below.
            </p>
          ) : (
            <p style={styles.intro}>{provider.instructions}</p>
          )}
          <GuidePanel platform={provider.platform} loginAvailable={false} />
          {fields.map((f, i) => renderField(f, i === 0))}
          {errorMsg && <p style={styles.error}>{errorMsg}</p>}
          <div style={styles.actions}>
            <button type="button" style={styles.cancel} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" style={styles.submit} disabled={!allFilled || connect.isPending}>
              {connect.isPending ? pendingLabel : submitLabel}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 24,
    width: 480,
    maxWidth: "95vw",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  close: {
    background: "transparent",
    color: "var(--body)",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    padding: 0,
    width: 28,
    height: 28,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  intro: {
    color: "var(--body)",
    fontSize: 13,
    margin: 0,
    lineHeight: 1.5,
  },
  hint: {
    color: "var(--body-faint)",
    fontSize: 12,
    margin: 0,
    lineHeight: 1.5,
  },
  label: {
    display: "flex",
    flexDirection: "column",
    fontSize: 13,
    color: "var(--body)",
    gap: 6,
  },
  optional: {
    color: "var(--body)",
    fontSize: 11,
    marginLeft: 4,
  },
  input: {
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    padding: "8px 10px",
    borderRadius: 6,
    fontSize: 14,
    fontFamily: "inherit",
  },
  path: {
    color: "var(--text)",
    fontFamily: "ui-monospace, monospace",
    fontSize: 12,
    background: "var(--surface-2)",
    padding: "1px 6px",
    borderRadius: 4,
  },
  error: {
    color: "var(--bad)",
    fontSize: 13,
    margin: 0,
    padding: "8px 12px",
    background: "color-mix(in srgb, var(--bad) 14%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--bad) 35%, var(--border))",
    borderRadius: 6,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 8,
  },
  cancel: {
    background: "transparent",
    color: "var(--body)",
    border: "1px solid var(--border)",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer",
  },
  submit: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "8px 20px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};
